import { Divider, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { PriceSuggestionPrice } from '@retail/price-suggestion/types';
import { PriceSuggestionPriority, PriceSuggestionType } from '@retail/products/types';
import { formatPrice, getPriceSuggestionType } from '@retail/products/utils';
import { InheritedIcon } from '@shared/custom-icons';
import { useTranslation } from 'react-i18next';

interface Props {
  priceSuggestionPriority: PriceSuggestionPriority[];
  availablePriceSuggestions: PriceSuggestionPrice[];
  activePriceSuggestionType?: PriceSuggestionType;
  markupPrice?: number;
  markupCoverageDegree?: number;
  isInheritedMarkup: boolean;
}

export function SalesPriceConditionPriority({
  priceSuggestionPriority,
  availablePriceSuggestions,
  activePriceSuggestionType,
  markupPrice,
  markupCoverageDegree,
  isInheritedMarkup,
}: Props) {
  const t = useAppTFunction();
  const theme = useTheme();
  const palette = theme.vars.palette;
  const { i18n } = useTranslation();

  const priceSourcePriority: PriceSuggestionPriority[] =
    markupCoverageDegree != null &&
    !priceSuggestionPriority.map((prio) => prio.priceSuggestionType).includes('MARKUP')
      ? [
          ...priceSuggestionPriority,
          { priceSuggestionType: 'MARKUP', priority: priceSuggestionPriority.length + 1 },
        ]
      : priceSuggestionPriority;

  return (
    <Stack
      border={`1px solid ${palette.divider}`}
      borderRadius={1}
      overflow="hidden"
      divider={<Divider />}
    >
      {priceSourcePriority.map(({ priority, priceSuggestionType }) => {
        const priceSuggestion = availablePriceSuggestions.find(
          (ps) => ps.priceSuggestionType === priceSuggestionType
        );
        return (
          <Stack
            key={priceSuggestionType}
            display="grid"
            gridTemplateColumns="2fr 1fr"
            alignItems="center"
            py={0.5}
            px={2}
            gap={3}
            bgcolor={
              priceSuggestionType === activePriceSuggestionType ||
              (!activePriceSuggestionType &&
                markupCoverageDegree != null &&
                priceSuggestionType === 'MARKUP')
                ? 'success.100'
                : 'inherit'
            }
          >
            <Stack flexDirection="row" gap={0.5} alignItems="center">
              <Typography variant="subtitle2" color="textSecondary" mb={-0.1}>
                {priority}.
              </Typography>

              {priceSuggestionType === 'MARKUP' ? (
                <Stack flexDirection="row" alignItems="center">
                  <Typography variant="body2" color="textSecondary">{`${t(
                    'markup.salesCoverage'
                  )}: ${markupCoverageDegree ?? '-'}%`}</Typography>
                  {isInheritedMarkup && (
                    <Tooltip title={t('calculus.inheritedMarkup')}>
                      <Stack>
                        <InheritedIcon sx={{ transform: 'scale(0.7)' }} />
                      </Stack>
                    </Tooltip>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {getPriceSuggestionType(t, priceSuggestionType)}
                </Typography>
              )}
            </Stack>

            <Typography variant="body2" textAlign="right">
              {t('myAssortment.productCard.price', {
                price: priceSuggestion?.price
                  ? formatPrice(priceSuggestion.price, i18n.language)
                  : markupPrice
                  ? formatPrice(markupPrice, i18n.language)
                  : '-',
              })}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}
